/**
 * Assessment Action - WHAT Section
 *
 * Edit mode UI for assigning an assessment.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-assessment.hbs
 *
 * Fields:
 * - reminderOnId: Selected assessment ID
 * - reminderOnType: "Assessment" or "Grouping" (sequence)
 * - defaultLifecycleStageId: Optional lifecycle stage to transition to
 * - removeItem: Whether to unassign after completion
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Stack } from 'src/components/Stack';
import {
  fetchAssessmentOptions,
  fetchAssessmentSequenceOptions,
} from 'src/models/assessment/assessment';
import { AssessmentsSequencesSelect } from 'src/shared/Forms/Selects/AssessmentsSequencesSelect';

import { ActionWhatSectionProps } from '../automationActions';
import { LifecycleTransitionField } from '../components';

import { resolveAssessmentAssignableName } from './assessmentActionUtils';

/**
 * Assessment WHAT Section - Edit mode
 */
export const AssessmentWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  return (
    <Stack spacing={2}>
      {/* Assessment Selection */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.assessment.assign')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <AssessmentsSequencesSelect
            name={`automations.${index}.reminderOnId`}
            value={automation.reminderOnId ?? ''}
            onChange={(item) => {
              if (item && item.type === 'grouping') {
                setFieldValue(`automations.${index}.reminderOnId`, item.id);
                setFieldValue(`automations.${index}.reminderOnType`, 'Grouping');
              } else if (item && item.type === 'assessment') {
                setFieldValue(`automations.${index}.reminderOnId`, item.id);
                setFieldValue(
                  `automations.${index}.reminderOnType`,
                  'Assessment'
                );
              } else {
                setFieldValue(`automations.${index}.reminderOnId`, '');
                setFieldValue(`automations.${index}.reminderOnType`, '');
              }
            }}
          />
        </Box>
      </Box>

      {/* Lifecycle Stage Transition (Optional) */}
      <LifecycleTransitionField
        name={`automations.${index}.defaultLifecycleStageId`}
      />

      {/* Unassign after completion */}
      <Box>
        <Checkbox
          label={t('automations.assessment.unassignAfterCompletion')}
          checked={Boolean(automation.removeItem)}
          onChange={(e) =>
            setFieldValue(`automations.${index}.removeItem`, e.target.checked)
          }
        />
      </Box>
    </Stack>
  );
};

/**
 * Assessment What Summary Component
 * Fetches assessment data to display the actual name
 */
export const AssessmentWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: assessments = [] } = useQuery({
    queryKey: ['assessments', 'options'],
    queryFn: fetchAssessmentOptions,
    staleTime: 60000,
  });

  const { data: sequences = [] } = useQuery({
    queryKey: ['assessmentSequences', 'options'],
    queryFn: fetchAssessmentSequenceOptions,
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.assessment.noAssessmentSelected')}</i>;
  }

  const assignableName = resolveAssessmentAssignableName(
    automation.reminderOnType,
    automation.reminderOnId,
    assessments,
    sequences
  );
  const assessmentName =
    assignableName ||
    `${automation.reminderOnType === 'Grouping' ? 'Sequence' : 'Assessment'} #${automation.reminderOnId}`;

  const unassignText = automation.removeItem
    ? t('automations.assessment.andUnassignAfterCompletion')
    : '';

  return (
    <>
      {t('automations.assessment.assign')} <b>{assessmentName}</b>
      {unassignText}
    </>
  );
};
